import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES, getProvincesForCountry, hasProvinceDropdown, normalizeCountryForSelect } from '../utils/countriesAndProvinces';
import { formatDateForHtmlInput } from '../utils/dateInput';
import './JuventusCardForm.css';

// Signature pad: canvas draw box
const SIGNATURE_WIDTH = 400;
const SIGNATURE_HEIGHT = 150;

const SignaturePad = ({ value, onChange, disabled, clearLabel }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = SIGNATURE_WIDTH;
    canvas.height = SIGNATURE_HEIGHT;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
    }
  }, []);

  useEffect(() => {
    if (value) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    if (disabled) return;
    isDrawing.current = true;
    lastPos.current = getPoint(e);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current || !lastPos.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    const pos = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPos.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
    onChange('');
  };

  return (
    <div className="signature-pad-wrap">
      <canvas
        ref={canvasRef}
        className="signature-pad"
        width={SIGNATURE_WIDTH}
        height={SIGNATURE_HEIGHT}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <button type="button" className="btn-signature-clear" onClick={clear} disabled={disabled}>
        {disabled ? '' : clearLabel}
      </button>
    </div>
  );
};

const TRANSLATIONS = {
  fr: {
    title: 'Demande Juventus Card',
    intro: 'Remplissez le formulaire ci-dessous pour demander votre Juventus Card. Les champs marqués * sont obligatoires.',
    titleLabel: 'Civilité *',
    titleMr: 'Monsieur',
    titleMrs: 'Madame',
    firstName: 'Prénom *',
    lastName: 'Nom *',
    lastNameHint: '(comme indiqué sur le document d\'identité / Codice Fiscale)',
    sex: 'Sexe *',
    sexM: 'M',
    sexF: 'F',
    dateOfBirth: 'Date de naissance * (jj/mm/aaaa)',
    birthPlace: 'Lieu de naissance *',
    birthCountry: 'Pays de naissance *',
    birthProvince: 'Prov. (code)',
    idSection: 'Document d\'identité',
    idType: 'Type de document *',
    idNumber: 'N° du document *',
    idExpiry: 'Date d\'expiration *',
    addressSection: 'Adresse de résidence',
    addressStreet: 'Rue / Adresse *',
    addressNumber: 'N° civ. *',
    postalCode: 'Code postal *',
    city: 'Ville *',
    province: 'Prov. (code)',
    country: 'Pays *',
    contactSection: 'Contact',
    phone: 'Téléphone fixe',
    mobile: 'Téléphone portable',
    email: 'Email *',
    minorSection: 'Si le demandeur a moins de 18 ans',
    isMinor: 'Le demandeur est mineur',
    parentName: 'Nom du parent / tuteur',
    parentSurname: 'Prénom du parent / tuteur',
    parentBirthPlace: 'Lieu de naissance du parent / tuteur',
    parentDateOfBirth: 'Date de naissance du parent / tuteur (jj/mm/aaaa)',
    declarations: 'Déclarations',
    acceptTerms: 'Je déclare avoir pris connaissance et accepter les Termes et Conditions de la Juventus Card (Allegato 2), publiés sur www.juventus.com, et avoir reçu copie du Code de conduite. *',
    acceptArt: 'J\'approuve spécifiquement les art. 3 (suspension), 5 (exclusion/révocation) et 11 (exclusion du droit de rétractation). *',
    declarationCriminal: 'Je déclare ne pas être sous le coup d\'interdiction d\'accès aux stades (art. 6 L. 401/1989), de mesures de prévention (L. 1423/1956), ni avoir été condamné pour des faits liés à des manifestations sportives. *',
    signaturePlace: 'Lieu de signature',
    signature: 'Signature *',
    signatureClear: 'Effacer',
    submit: 'Envoyer la demande',
    back: '← Retour à la page Juventus Card',
    success: 'Votre demande a bien été enregistrée. Elle sera traitée par le club.',
    error: 'Une erreur est survenue. Veuillez réessayer.',
    lang: 'Langue du formulaire',
    selectCountry: 'Sélectionnez un pays',
    selectProvince: 'Sélectionnez...',
  },
  it: {
    title: 'Richiesta Juventus Card',
    intro: 'Compilare il modulo per richiedere la Juventus Card. I campi contrassegnati * sono obbligatori.',
    titleLabel: 'Titolo *',
    titleMr: 'Sig.',
    titleMrs: 'Sig.ra',
    firstName: 'Nome *',
    lastName: 'Cognome *',
    lastNameHint: '(come indicato sul Codice Fiscale)',
    sex: 'Sesso *',
    sexM: 'M',
    sexF: 'F',
    dateOfBirth: 'Data di nascita * (gg/mm/aaaa)',
    birthPlace: 'Luogo di nascita *',
    birthCountry: 'Stato di nascita *',
    birthProvince: 'Prov.',
    idSection: 'Documento d\'identità',
    idType: 'Tipologia documento *',
    idNumber: 'N. documento *',
    idExpiry: 'Scadenza *',
    addressSection: 'Indirizzo di residenza',
    addressStreet: 'Indirizzo *',
    addressNumber: 'N° civ. *',
    postalCode: 'CAP *',
    city: 'Città *',
    province: 'Prov.',
    country: 'Stato *',
    contactSection: 'Contatti',
    phone: 'Telefono',
    mobile: 'Cellulare',
    email: 'Email *',
    minorSection: 'Se il richiedente è minore di 18 anni',
    isMinor: 'Il richiedente è minorenne',
    parentName: 'Nome del genitore/tutore',
    parentSurname: 'Cognome del genitore/tutore',
    parentBirthPlace: 'Luogo di nascita del genitore/tutore',
    parentDateOfBirth: 'Data di nascita del genitore/tutore (gg/mm/aaaa)',
    declarations: 'Dichiarazioni',
    acceptTerms: 'Dichiaro di aver preso visione e accettare i Termini e Condizioni della Juventus Card (Allegato 2), pubblicati su www.juventus.com, e di aver ricevuto copia del Codice di condotta. *',
    acceptArt: 'Approvazione specifica degli artt. 3, 5 e 11. *',
    declarationCriminal: 'Dichiarazione sostitutiva: non essere destinatario di provvedimenti DASPO, misure di prevenzione, né condannato per reati in occasione di manifestazioni sportive. *',
    signaturePlace: 'Luogo e data',
    signature: 'Firma *',
    signatureClear: 'Cancella',
    submit: 'Invia richiesta',
    back: '← Torna alla pagina Juventus Card',
    success: 'La richiesta è stata registrata. Sarà esaminata dal club.',
    error: 'Si è verificato un errore. Riprovare.',
    lang: 'Lingua del modulo',
    selectCountry: 'Selezionare un paese',
    selectProvince: 'Selezionare...',
  },
  en: {
    title: 'Juventus Card Request',
    intro: 'Fill in the form below to request your Juventus Card. Fields marked * are required.',
    titleLabel: 'Title *',
    titleMr: 'Mr',
    titleMrs: 'Mrs',
    firstName: 'First name *',
    lastName: 'Last name *',
    lastNameHint: '(as on ID document / Codice Fiscale)',
    sex: 'Sex *',
    sexM: 'M',
    sexF: 'F',
    dateOfBirth: 'Date of birth * (dd/mm/yyyy)',
    birthPlace: 'Place of birth *',
    birthCountry: 'Country of birth *',
    birthProvince: 'Province (code)',
    idSection: 'ID document',
    idType: 'Document type *',
    idNumber: 'Document number *',
    idExpiry: 'Expiry date *',
    addressSection: 'Residence address',
    addressStreet: 'Street address *',
    addressNumber: 'Street number *',
    postalCode: 'Postal code *',
    city: 'City *',
    province: 'Province (code)',
    country: 'Country *',
    contactSection: 'Contact',
    phone: 'Phone',
    mobile: 'Mobile',
    email: 'Email *',
    minorSection: 'If the applicant is under 18',
    isMinor: 'The applicant is a minor',
    parentName: 'Parent/guardian first name',
    parentSurname: 'Parent/guardian last name',
    parentBirthPlace: 'Parent/guardian place of birth',
    parentDateOfBirth: 'Parent/guardian date of birth (dd/mm/yyyy)',
    declarations: 'Declarations',
    acceptTerms: 'I declare that I have read and accept the Juventus Card Terms and Conditions (Allegato 2), published on www.juventus.com, and have received a copy of the Code of Conduct. *',
    acceptArt: 'I specifically approve art. 3 (suspension), 5 (exclusion/revocation) and 11 (withdrawal right exclusion). *',
    declarationCriminal: 'I declare that I am not subject to stadium ban (art. 6 L. 401/1989), preventive measures (L. 1423/1956), nor have I been convicted for offences related to sports events. *',
    signaturePlace: 'Place of signature',
    signature: 'Signature *',
    signatureClear: 'Clear',
    submit: 'Submit request',
    back: '← Back to Juventus Card page',
    success: 'Your request has been recorded. It will be processed by the club.',
    error: 'An error occurred. Please try again.',
    lang: 'Form language',
    selectCountry: 'Select a country',
    selectProvince: 'Select...',
  },
};

const ID_TYPES = {
  fr: ['Carte d\'identité', 'Passeport'],
  it: ['Carta d\'identità', 'Passaporto'],
  en: ['Identity card', 'Passport'],
};

function formatDateForApi(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
}

const PASSPORT_IDENTITY_TYPES = new Set(['Passeport', 'Passaporto', 'Passport']);

function mapUserIdentityTypeToFormOption(userType, lang) {
  const options = ID_TYPES[lang] || ID_TYPES.fr;
  if (!userType) return options[0];
  if (PASSPORT_IDENTITY_TYPES.has(userType)) return options[1];
  const exact = options.find((o) => o === userType);
  if (exact) return exact;
  return options[0];
}

function civiliteToTitle(civilite) {
  if (!civilite) return 'Sig.';
  const c = String(civilite).trim().toLowerCase();
  if (c === 'mme' || c === 'madame' || c === 'sig.ra') return 'Sig.ra';
  return 'Sig.';
}

function sexeToFormSex(sexe) {
  if (sexe === 'Femme') return 'F';
  return 'M';
}

/** Merge profile from GET /auth/me into Juventus card form state */
function profileToFormPatch(profile, lang) {
  if (!profile) return null;
  const bp = profile.birthPlace || {};
  const addr = profile.address || {};
  const idn = profile.identity || {};
  const parent = profile.parentInfo || {};

  const birthCountryRaw = bp.country || profile.address?.country || '';
  const birthCountry = normalizeCountryForSelect(birthCountryRaw, COUNTRIES) || 'Suisse';

  const patch = {
    title: civiliteToTitle(profile.civilite),
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    sex: sexeToFormSex(profile.sexe),
    dateOfBirth: formatDateForHtmlInput(profile.dateOfBirth),
    birthPlace: bp.city || '',
    birthCountry,
    birthProvince: bp.canton || '',
    idDocumentType: mapUserIdentityTypeToFormOption(idn.type, lang),
    idNumber: idn.number || '',
    idExpiry: formatDateForHtmlInput(idn.expiryDate),
    addressStreet: addr.street || '',
    addressStreetNumber: addr.streetNumber || '',
    addressCity: addr.city || '',
    addressPostalCode: addr.postalCode || '',
    addressProvince: addr.canton || '',
    addressCountry: normalizeCountryForSelect(addr.country || '', COUNTRIES) || 'Suisse',
    phone: '',
    mobile: profile.phone || '',
    email: profile.email || '',
    isMinor: !!profile.isMinor,
    parentFirstName: parent.lastName || '',
    parentLastName: parent.firstName || '',
    parentBirthPlace: [parent.birthCity, parent.birthCanton, parent.birthCountry].filter(Boolean).join(', ') || '',
    parentDateOfBirth: formatDateForHtmlInput(parent.dateOfBirth),
  };

  return patch;
}

const JuventusCardForm = () => {
  const { user: authUser } = useAuth();
  const [lang, setLang] = useState('fr');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Sig.',
    firstName: '',
    lastName: '',
    sex: 'M',
    dateOfBirth: '',
    birthPlace: '',
    birthCountry: 'Suisse',
    birthProvince: '',
    idDocumentType: 'Carte d\'identité',
    idNumber: '',
    idExpiry: '',
    addressStreet: '',
    addressStreetNumber: '',
    addressCity: '',
    addressPostalCode: '',
    addressProvince: '',
    addressCountry: 'Suisse',
    phone: '',
    mobile: '',
    email: '',
    isMinor: false,
    parentFirstName: '',
    parentLastName: '',
    parentBirthPlace: '',
    parentDateOfBirth: '',
    acceptTerms: false,
    acceptArt31411: false,
    declarationNoDASPO: false,
    signaturePlace: '',
    signatureImage: '',
  });

  useEffect(() => {
    if (!authUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (cancelled) return;
        const profile = data.user;
        if (!profile) return;
        const pl = profile.preferredLanguage;
        const formLang = pl === 'it' ? 'it' : 'fr';
        if (pl === 'it' || pl === 'fr') setLang(pl);
        const patch = profileToFormPatch(profile, formLang);
        if (!patch) return;
        setFormData((prev) => ({ ...prev, ...patch }));
      } catch {
        // Profil non chargé : le formulaire reste aux valeurs par défaut
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authUser?.id, authUser?.email]);

  const t = TRANSLATIONS[lang] || TRANSLATIONS.fr;
  const idTypeOptions = ID_TYPES[lang] || ID_TYPES.fr;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Réinitialiser la province quand le pays change (liste différente selon pays)
      if (name === 'birthCountry') next.birthProvince = '';
      if (name === 'addressCountry') next.addressProvince = '';
      return next;
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.acceptTerms || !formData.acceptArt31411 || !formData.declarationNoDASPO) {
      setError(lang === 'fr' ? 'Veuillez accepter toutes les déclarations obligatoires.' : lang === 'it' ? 'Accettare tutte le dichiarazioni obbligatorie.' : 'Please accept all required declarations.');
      return;
    }
    if (formData.isMinor && (!formData.parentFirstName || !formData.parentLastName || !formData.parentBirthPlace || !formData.parentDateOfBirth)) {
      setError(lang === 'fr' ? 'Veuillez remplir les informations du parent/tuteur pour un demandeur mineur.' : lang === 'it' ? 'Compilare i dati del genitore/tutore per un minorenne.' : 'Please fill in parent/guardian details for a minor applicant.');
      return;
    }
    if (!formData.signatureImage) {
      setError(lang === 'fr' ? 'Veuillez signer dans la zone prévue.' : lang === 'it' ? 'Si prega di firmare nell\'apposito spazio.' : 'Please sign in the box provided.');
      return;
    }

    const payload = {
      title: formData.title,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      sex: formData.sex,
      dateOfBirth: formatDateForApi(formData.dateOfBirth),
      birthPlace: formData.birthPlace.trim(),
      birthCountry: formData.birthCountry.trim(),
      birthProvince: formData.birthProvince.trim(),
      idDocumentType: formData.idDocumentType,
      idNumber: formData.idNumber.trim(),
      idExpiry: formatDateForApi(formData.idExpiry),
      addressStreet: formData.addressStreet.trim(),
      addressStreetNumber: formData.addressStreetNumber.trim(),
      addressCity: formData.addressCity.trim(),
      addressPostalCode: formData.addressPostalCode.trim(),
      addressProvince: formData.addressProvince.trim(),
      addressCountry: formData.addressCountry.trim(),
      phone: formData.phone.trim(),
      mobile: formData.mobile.trim(),
      email: formData.email.trim(),
      isMinor: !!formData.isMinor,
      parentFirstName: formData.parentFirstName.trim(),
      parentLastName: formData.parentLastName.trim(),
      parentBirthPlace: formData.parentBirthPlace.trim(),
      parentDateOfBirth: formData.isMinor && formData.parentDateOfBirth ? formatDateForApi(formData.parentDateOfBirth) : null,
      acceptTerms: true,
      acceptArt31411: true,
      declarationNoDASPO: true,
      signaturePlace: formData.signaturePlace.trim(),
      signatureImage: formData.signatureImage || '',
      formLanguage: lang,
    };

    setLoading(true);
    try {
      await api.post('/juventus-card-requests', payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="juventus-card-form-page">
        <div className="container">
          <h1 className="page-title">{t.title}</h1>
          <div className="juventus-card-form-success">
            <p>{t.success}</p>
            <Link to="/juventus-card" className="btn-back-link">{t.back}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="juventus-card-form-page">
      <div className="container">
        <div className="form-lang-select">
          <label>{t.lang}:</label>
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="fr">Français</option>
            <option value="it">Italiano</option>
            <option value="en">English</option>
          </select>
        </div>
        <h1 className="page-title">{t.title}</h1>
        <p className="form-intro">{t.intro}</p>

        <form onSubmit={handleSubmit} className="juventus-card-form">
          {error && <div className="form-error">{error}</div>}

          <section className="form-section">
            <h2>{t.titleLabel}</h2>
            <div className="form-row">
              <label className="label-radio">
                <input type="radio" name="title" value="Sig." checked={formData.title === 'Sig.'} onChange={handleChange} />
                <span>{t.titleMr}</span>
              </label>
              <label className="label-radio">
                <input type="radio" name="title" value="Sig.ra" checked={formData.title === 'Sig.ra'} onChange={handleChange} />
                <span>{t.titleMrs}</span>
              </label>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">{t.firstName}</label>
                <input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">{t.lastName}</label>
                <input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
              </div>
            </div>
            <p className="field-hint">{t.lastNameHint}</p>
            <div className="form-row form-row-inline">
              <div className="form-group">
                <label>{t.sex}</label>
                <div className="radio-inline">
                  <label className="label-radio"><input type="radio" name="sex" value="M" checked={formData.sex === 'M'} onChange={handleChange} /> {t.sexM}</label>
                  <label className="label-radio"><input type="radio" name="sex" value="F" checked={formData.sex === 'F'} onChange={handleChange} /> {t.sexF}</label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="dateOfBirth">{t.dateOfBirth}</label>
                <input id="dateOfBirth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="birthPlace">{t.birthPlace}</label>
                <input id="birthPlace" name="birthPlace" value={formData.birthPlace} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="birthCountry">{t.birthCountry}</label>
                <select
                  id="birthCountry"
                  name="birthCountry"
                  value={formData.birthCountry}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.selectCountry}</option>
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="birthProvince">{t.birthProvince}</label>
                {hasProvinceDropdown(formData.birthCountry) ? (
                  <select
                    id="birthProvince"
                    name="birthProvince"
                    value={formData.birthProvince}
                    onChange={handleChange}
                  >
                    <option value="">{t.selectProvince}</option>
                    {getProvincesForCountry(formData.birthCountry).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="birthProvince"
                    name="birthProvince"
                    value={formData.birthProvince}
                    onChange={handleChange}
                    placeholder="–"
                  />
                )}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.idSection}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="idDocumentType">{t.idType}</label>
                <select id="idDocumentType" name="idDocumentType" value={formData.idDocumentType} onChange={handleChange} required>
                  {idTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="idNumber">{t.idNumber}</label>
                <input id="idNumber" name="idNumber" value={formData.idNumber} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="idExpiry">{t.idExpiry}</label>
                <input id="idExpiry" name="idExpiry" type="date" value={formData.idExpiry} onChange={handleChange} required />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.addressSection}</h2>
            <div className="form-row">
              <div className="form-group form-group-wide">
                <label htmlFor="addressStreet">{t.addressStreet}</label>
                <input id="addressStreet" name="addressStreet" value={formData.addressStreet} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressStreetNumber">{t.addressNumber}</label>
                <input id="addressStreetNumber" name="addressStreetNumber" value={formData.addressStreetNumber} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="addressCity">{t.city}</label>
                <input id="addressCity" name="addressCity" value={formData.addressCity} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressPostalCode">{t.postalCode}</label>
                <input id="addressPostalCode" name="addressPostalCode" value={formData.addressPostalCode} onChange={handleChange} required />
              </div>
              <div className="form-group form-group-small">
                <label htmlFor="addressProvince">{t.province}</label>
                {hasProvinceDropdown(formData.addressCountry) ? (
                  <select
                    id="addressProvince"
                    name="addressProvince"
                    value={formData.addressProvince}
                    onChange={handleChange}
                  >
                    <option value="">{t.selectProvince}</option>
                    {getProvincesForCountry(formData.addressCountry).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="addressProvince"
                    name="addressProvince"
                    value={formData.addressProvince}
                    onChange={handleChange}
                    placeholder="–"
                  />
                )}
              </div>
              <div className="form-group">
                <label htmlFor="addressCountry">{t.country}</label>
                <select
                  id="addressCountry"
                  name="addressCountry"
                  value={formData.addressCountry}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.selectCountry}</option>
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.contactSection}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">{t.phone}</label>
                <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="mobile">{t.mobile}</label>
                <input id="mobile" name="mobile" type="tel" value={formData.mobile} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="email">{t.email}</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2>{t.minorSection}</h2>
            <label className="label-checkbox">
              <input type="checkbox" name="isMinor" checked={formData.isMinor} onChange={handleChange} />
              <span>{t.isMinor}</span>
            </label>
            {formData.isMinor && (
              <div className="form-row parent-fields">
                <div className="form-group">
                  <label htmlFor="parentFirstName">{t.parentName}</label>
                  <input id="parentFirstName" name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="parentLastName">{t.parentSurname}</label>
                  <input id="parentLastName" name="parentLastName" value={formData.parentLastName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="parentBirthPlace">{t.parentBirthPlace}</label>
                  <input id="parentBirthPlace" name="parentBirthPlace" value={formData.parentBirthPlace} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="parentDateOfBirth">{t.parentDateOfBirth}</label>
                  <input id="parentDateOfBirth" name="parentDateOfBirth" type="date" value={formData.parentDateOfBirth} onChange={handleChange} />
                </div>
              </div>
            )}
          </section>

          <section className="form-section">
            <h2>{t.declarations}</h2>
            <label className="label-checkbox">
              <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} required />
              <span>{t.acceptTerms}</span>
            </label>
            <label className="label-checkbox">
              <input type="checkbox" name="acceptArt31411" checked={formData.acceptArt31411} onChange={handleChange} required />
              <span>{t.acceptArt}</span>
            </label>
            <label className="label-checkbox">
              <input type="checkbox" name="declarationNoDASPO" checked={formData.declarationNoDASPO} onChange={handleChange} required />
              <span>{t.declarationCriminal}</span>
            </label>
            <div className="form-group">
              <label htmlFor="signaturePlace">{t.signaturePlace}</label>
              <input id="signaturePlace" name="signaturePlace" value={formData.signaturePlace} onChange={handleChange} placeholder="ex. Montreux" />
            </div>
            <div className="form-group">
              <label>{t.signature}</label>
              <SignaturePad
                value={formData.signatureImage}
                onChange={(dataUrl) => setFormData(prev => ({ ...prev, signatureImage: dataUrl }))}
                disabled={loading}
                clearLabel={t.signatureClear}
              />
            </div>
          </section>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (lang === 'fr' ? 'Envoi...' : lang === 'it' ? 'Invio...' : 'Sending...') : t.submit}
            </button>
            <Link to="/juventus-card" className="btn-back-link">{t.back}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JuventusCardForm;
